import { NotificationsNotificationEnvelopeDto } from '@mi-front/api-v2';
import { MessageDescriptor } from 'react-intl';
import { NotificationWorkflowCategory } from './notification-workflow-category';

/** Keep in sync with `NotificationSeverity` in notification-severity.ts (no import cycle). */
export type NotificationWorkflowSeverity = 'error' | 'warning' | 'success' | 'info';

/** Knock workflow keys known to the API notification envelope union. */
export type NotificationWorkflowKey = NotificationsNotificationEnvelopeDto['workflowKey'];

/** Envelope member for a given workflow key (includes `workflowKey` + typed `data`). */
export type NotificationEnvelopeForKey<K extends NotificationWorkflowKey> = Extract<
  NotificationsNotificationEnvelopeDto,
  { workflowKey: K }
>;

/** Payload `data` for a given workflow key, from the API envelope. */
export type NotificationWorkflowData<K extends NotificationWorkflowKey> =
  NotificationEnvelopeForKey<K>['data'];

export type NotificationWorkflowAction = {
  name: string;
  label: MessageDescriptor;
  url: string;
};

/**
 * Which Knock channel types this workflow actually delivers on.
 * Preference toggles and persisted `channel_types` only include supported channels.
 */
export type NotificationWorkflowChannels = {
  /** Knock `in_app_feed` channel. */
  inApp: boolean;
  /** Knock `email` channel. */
  email: boolean;
};

/**
 * Feature-owned Knock workflow behavior for the in-app feed, toasts, and preferences.
 * Descriptors must not import Knock FeedItem — the shell passes `item.data`.
 * Call sites treat data as untyped Knock payload; authors get typed data via `defineNotificationWorkflow`.
 * `category` is UI grouping only — Knock prefs are still saved per workflow key.
 */
export type NotificationWorkflow = {
  key: NotificationWorkflowKey;
  /** Knock category id; used to group preference rows (not persisted as category prefs). */
  category: NotificationWorkflowCategory;
  /** Channels this workflow supports; unsupported toggles are hidden and not persisted. */
  channels: NotificationWorkflowChannels;
  severity: NotificationWorkflowSeverity;
  /**
   * Whether realtime items show an in-app toast.
   * When omitted: true for error/warning, false for success/info.
   */
  toast?: boolean;
  preference: {
    title: MessageDescriptor;
    description: MessageDescriptor;
  };
  /** Primary click / toast link from workflow payload data. */
  actionUrl: (data: Record<string, unknown>) => string | null;
  /** Extra feed buttons (e.g. open video). */
  actions?: (data: Record<string, unknown>) => NotificationWorkflowAction[];
};

type NotificationWorkflowDefinition<K extends NotificationWorkflowKey> = {
  key: K;
  category: NotificationWorkflowCategory;
  channels: NotificationWorkflowChannels;
  severity: NotificationWorkflowSeverity;
  toast?: boolean;
  preference: {
    title: MessageDescriptor;
    description: MessageDescriptor;
  };
  actionUrl: (data: NotificationWorkflowData<K>) => string | null;
  actions?: (data: NotificationWorkflowData<K>) => NotificationWorkflowAction[];
};

/**
 * Declares a workflow descriptor typed from `NotificationsNotificationEnvelopeDto`.
 * Returns the registry shape so Knock's untyped `item.data` can be passed at call sites.
 */
export function defineNotificationWorkflow<K extends NotificationWorkflowKey>(
  workflow: NotificationWorkflowDefinition<K>,
): NotificationWorkflow {
  // Knock feed `item.data` is untyped at call sites; authors still get typed `data` above.
  return workflow as unknown as NotificationWorkflow;
}

/**
 * Reads the sole key from a Kubb single-member `*WorkflowKeyEnum` (avoids duplicating the literal).
 */
export function notificationWorkflowKeyFromEnum<E extends Record<string, NotificationWorkflowKey>>(
  workflowKeyEnum: E,
): E[keyof E] {
  return Object.values(workflowKeyEnum)[0] as E[keyof E];
}
